import * as React from 'react';

import { compose, setDisplayName, withHandlers, withState } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { Box, Button } from '@coursera/coursera-ui';

import thirdPartyAuth, { SSOTypes } from 'bundles/third-party-auth/lib';
import type { SSO_TYPE } from 'bundles/third-party-auth/lib';
import { USER_EMAIL_REGEX_PATTERN, USER_PASSWORD_REGEX_PATTERN } from 'bundles/user-account/common/constants';
import EmailField from 'bundles/user-account/components/EmailField';
import ErrorMessage from 'bundles/user-account/components/ErrorMessage';
import PasswordField from 'bundles/user-account/components/PasswordField';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

import _t from 'i18n!nls/user-account';

type Message = {
  primary: string;
  secondary: string | React.ReactElement<typeof FormattedHTMLMessage>;
};

type MessageReturnType = {
  noEmailAvailable: Message;
  existingCourseraAccount: Message;
  notAuthorized: Message;
  unableToGetProfile: Message;
  unknownStatus: Message;
};

const modeMessages = (): MessageReturnType => ({
  noEmailAvailable: {
    primary: _t('Almost there!'),
    secondary: _t("We'll need your email to complete your account registration."),
  },
  existingCourseraAccount: {
    primary: _t('You already have an account with this email address.'),
    // TODO make this link about to route locally instead of page refresh
    secondary: (
      <FormattedHTMLMessage
        message={_t(
          'Please enter your Coursera password below to log into your account. If you can’t remember it, {resetPasswordLink}.'
        )}
        resetPasswordLink={
          <a href="/reset" target="resetpw">
            {_t('Reset Password')}
          </a>
        }
      />
    ),
  },
  notAuthorized: {
    primary: _t('Something went wrong.'),
    secondary: _t('Please try again and be sure to authorize the Coursera app.'),
  },
  unableToGetProfile: {
    primary: _t('Unable to get profile.'),
    secondary: _t('Please try again.'),
  },
  unknownStatus: {
    primary: _t('Something went wrong.'),
    secondary: _t('Please try again.'),
  },
});

const defaultMessage = () => ({
  primary: _t('An unexpected error occurred.'),
  secondary: _t('Please try again later.'),
});

function getTranslations(): { [name: string]: string } {
  return {
    CONTINUE: _t('Continue'),
  };
}

type PropsFromCaller = {
  completeMode:
    | 'noEmailAvailable'
    | 'existingCourseraAccount'
    | 'notAuthorized'
    | 'unableToGetProfile'
    | 'unknownStatus';
};

type PropsToComponent = PropsFromCaller & {
  email: string;
  password: string;
  errorCode: string;
  router: InjectedRouter;
  onChange: (x0: React.ChangeEvent<HTMLInputElement>) => void;
  tryCompleting: () => void;
  setEmail: (value: string) => void;
  setPassword: (value: string) => void;
  setErrorCode: (value: string) => void;
  onSuccess: () => void;
};
/**
 * Complete a registration or account linking with a social account.
 *
 * For now only supports Facebook and Apple.
 */
function CompleteForm({ completeMode, onChange, errorCode, email, password, tryCompleting }: PropsToComponent) {
  const message = modeMessages()[completeMode] || defaultMessage();
  const _T = getTranslations();

  return (
    <Box rootClassName="rc-CompleteForm" flexDirection="column">
      <h2>{message.primary}</h2>
      <p>{message.secondary}</p>
      {errorCode && <ErrorMessage errorCode={errorCode} />}
      <div data-js="control-group login-password-phase">
        {completeMode === 'noEmailAvailable' && (
          <EmailField onChange={onChange} value={email} key="email" pattern={USER_EMAIL_REGEX_PATTERN} />
        )}
        {completeMode === 'existingCourseraAccount' && (
          <PasswordField onChange={onChange} value={password} key="password" pattern={USER_PASSWORD_REGEX_PATTERN} />
        )}
      </div>
      <Button onClick={tryCompleting} type="primary" label={_T.CONTINUE} />
    </Box>
  );
}

export default compose<PropsToComponent, PropsFromCaller>(
  setDisplayName('CompleteFormHOC'),
  connectToRouter((router) => ({ router })),
  withState('email', 'setEmail', ''),
  withState('password', 'setPassword', ''),
  withState('errorCode', 'setErrorCode', null),
  withHandlers<PropsToComponent, { onChange: Function; tryCompleting: Function }>({
    onChange:
      ({ setEmail, setPassword }) =>
      (ev: React.ChangeEvent<HTMLInputElement>) => {
        const { value, name } = ev.target;
        if (name === 'email') {
          setEmail(value);
        } else if (name === 'password') {
          setPassword(value);
        }
      },
    tryCompleting:
      ({ email, password, setErrorCode, onSuccess, router }) =>
      () => {
        const authType: SSO_TYPE | undefined = router?.location?.query?.authType;
        if (!authType) {
          return;
        }

        Instrumentation.requestEmailSubmit(authType);
        // We combine authData with the form data so we have all supplementary information needed
        // to complete the registration, such as the email or the password
        const emailFormatted = email.trim().length === 0 ? undefined : email.trim();
        const passwordFormatted = password.trim().length === 0 ? undefined : password.trim();

        const additionalData = { email: emailFormatted, password: passwordFormatted };
        thirdPartyAuth
          .connect(authType, additionalData)
          .then((response) => {
            Instrumentation.thirdPartyAuth(authType, response).fin(() => {
              onSuccess();
            });
          })
          .fail((error) => {
            Instrumentation.thirdPartyError(authType, error);

            const code = error?.code;

            // We need to support old cases so I'll be supporting accountLinkedToSocialAccount and
            // existingCourseraAccountNoPasswordSet until we fully deploy this
            if (['accountLinkedToSocialAccount', 'existingCourseraAccountNoPasswordSet'].includes(code)) {
              const query = {
                ...router.location.query,
                authMode: 'link',
                authType: SSOTypes.FACEBOOK,
                serviceCode: error?.existingAccountType,
              };

              router.push({
                pathname: router.location.pathname,
                params: router.params,
                query,
              });
            } else {
              setErrorCode(error?.code);
            }
          })
          .done();
      },
  })
)(CompleteForm);
